import { fieldOrder, fieldSpec } from '../../domain/fields.js';
import { labelOf, type EntityType } from '../../domain/labels.js';
import { countWord } from '../../domain/pluralise.js';
import { rowLabel } from '../../domain/rowLabel.js';
import { ENTITY_TYPE_ORDER, ENTITY_TYPES } from '../../domain/entityTypes.js';
import type { PushPreview } from './pushSummary.js';

/**
 * The push review's record nav: every record the dry run writes to the environment on screen,
 * found straight in the one `push.preview` response (its `ops`, task 1's addition) — no read
 * per record. Built apart from the modal that draws it, same as `pushReviewView.ts`.
 */

/** One operation of a preview's `ops` — the dry run's rendered record, not yet applied. */
export type PreviewOp = PushPreview['ops'][number];

export type RecordEntry = {
    op: PreviewOp;
    /** `${entityType}:${entityKey}` — unique across the whole preview. */
    id: string;
    /** A game whose own studio is written in the same push, drawn a step in under it. */
    child: boolean;
};

/** The `id` its parent record would have, for a child type; null for a root. */
const parentIdOf = (entry: Pick<RecordEntry, 'op'>): string | null => {
    const def = ENTITY_TYPES[entry.op.entityType as EntityType];
    if (!def?.parentType) return null;
    const parentKey = def.parentKeyOf(entry.op.entityKey);
    return parentKey === null ? null : `${def.parentType}:${parentKey}`;
};

/**
 * Each record, then the records written here whose parent it is, a step in under it — the order
 * a push actually applies them in. A record whose parent is not itself written here stays at the
 * top level. Otherwise the dry run's own order (parent types before child types, each sorted by
 * key) is kept. The parent relation is the type registry's.
 */
const ordered = (entries: readonly Omit<RecordEntry, 'child'>[]): RecordEntry[] => {
    const ids = new Set(entries.map((entry) => entry.id));
    const childrenOf = new Map<string, Omit<RecordEntry, 'child'>[]>();
    const top: Omit<RecordEntry, 'child'>[] = [];
    for (const entry of entries) {
        const parent = parentIdOf(entry);
        if (parent !== null && ids.has(parent)) childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), entry]);
        else top.push(entry);
    }
    return top.flatMap((entry) => [
        { ...entry, child: false },
        ...(childrenOf.get(entry.id) ?? []).map((child) => ({ ...child, child: true })),
    ]);
};

/** Every record the dry run writes to one environment, studios before their own games. */
export const recordEntries = (ops: readonly PreviewOp[] | null | undefined): RecordEntry[] =>
    ordered((ops ?? []).map((op) => ({ op, id: `${op.entityType}:${op.entityKey}` })));

/** What a record is called in the nav and the pane — `rowLabel`'s name — and whether that is its key. */
export const recordName = (entry: Pick<RecordEntry, 'op'>): { name: string; isKey: boolean } => {
    const label = rowLabel(entry.op.title, entry.op.entityKey);
    return { name: label.label, isKey: label.labelIsId };
};

/** The nav's search: every word of the query somewhere in the title or the key. */
export const searchRecords = (entries: readonly RecordEntry[], query: string): RecordEntry[] => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [...entries];
    return entries.filter((entry) => {
        const haystack = `${entry.op.title ?? ''} ${entry.op.entityKey}`.toLowerCase();
        return words.every((word) => haystack.includes(word));
    });
};

/**
 * A field some update writes, for "only records changing <field>". Fields are per type — a
 * game's Bet values is not a studio's — so the id carries the type. A create writes every
 * field, so it counts towards none.
 */
export type FieldOption = { id: string; type: EntityType; label: string; records: number };

const fieldId = (type: EntityType, field: string): string => `${type}:${field}`;

/** The fields present across the records' patches, by type in registry order, with how many records write each. */
export const fieldOptions = (entries: readonly RecordEntry[]): FieldOption[] => {
    const byId = new Map<string, FieldOption & { rank: number }>();
    for (const entry of entries) {
        if (entry.op.op === 'create') continue;
        const type = entry.op.entityType as EntityType;
        for (const field of Object.keys(entry.op.patch)) {
            const id = fieldId(type, field);
            const known = byId.get(id);
            if (known) {
                known.records += 1;
                continue;
            }
            byId.set(id, { id, type, label: fieldSpec(type, field).label, records: 1, rank: fieldOrder(type, field) });
        }
    }
    return [...byId.values()]
        .sort(
            (a, b) =>
                ENTITY_TYPE_ORDER.indexOf(a.type) - ENTITY_TYPE_ORDER.indexOf(b.type) ||
                a.rank - b.rank ||
                a.label.localeCompare(b.label),
        )
        .map(({ rank: _rank, ...option }) => option);
};

/** Only the records that change `field` (a `FieldOption` id); every record where none is picked. */
export const recordsChanging = (entries: readonly RecordEntry[], field: string | null): RecordEntry[] =>
    field === null
        ? [...entries]
        : entries.filter(
              (entry) =>
                  entry.op.op !== 'create' &&
                  Object.keys(entry.op.patch).some((key) => fieldId(entry.op.entityType as EntityType, key) === field),
          );

/** The nav and the pane, narrowed by the search and the field filter together. */
export const visibleRecords = (
    entries: readonly RecordEntry[],
    filter: { query: string; field: string | null },
): RecordEntry[] => recordsChanging(searchRecords(entries, filter.query), filter.field);

/**
 * The "only records changing" picker's groups: one per type when the push writes more than
 * one (a game's Bet values and a studio's Name are two options and neither name says whose),
 * ungrouped otherwise. In `fieldOptions`' order.
 */
export const fieldGroups = (options: readonly FieldOption[]): { group: string; items: FieldOption[] }[] => {
    const types = new Set(options.map((option) => option.type));
    const groups = new Map<string, FieldOption[]>();
    for (const option of options) {
        const group = types.size > 1 ? labelOf(option.type).singular : '';
        groups.set(group, [...(groups.get(group) ?? []), option]);
    }
    return [...groups].map(([group, items]) => ({ group, items }));
};

/** What a narrowed nav says it shows: `4 of 6 records`; null while it shows them all. */
export const filteredNote = (shown: number, total: number): string | null =>
    shown < total ? `${shown} of ${countWord(total, 'record')}` : null;

/** The records drawn a step in, whose own parent is also on screen. */
export const nestedRecords = (visible: readonly RecordEntry[]): ReadonlySet<string> => {
    const ids = new Set(visible.map((entry) => entry.id));
    return new Set(visible.filter((entry) => entry.child && ids.has(parentIdOf(entry) ?? '')).map((entry) => entry.id));
};

/** The parent records (by `id`) one of whose children is drawn a step in — the nav's "collapse all" groups. */
export const navGroups = (entries: readonly RecordEntry[]): ReadonlySet<string> =>
    new Set(entries.filter((entry) => entry.child).map((entry) => parentIdOf(entry)!));

/** The nav, with a collapsed parent's own children hidden under it; `collapsed` holds parent `id`s. */
export const shownRecords = (visible: readonly RecordEntry[], collapsed: ReadonlySet<string>): RecordEntry[] =>
    visible.filter((entry) => !entry.child || !collapsed.has(parentIdOf(entry) ?? ''));

/** A list that stays quick at a thousand writes; the search narrows past it. */
export const RECORD_LIST_LIMIT = 200;

/** The nav's foot when the cap cut it short. */
export const cappedNote = (shown: number, total: number): string | null =>
    total > shown ? `First ${shown} of ${total} · search to narrow` : null;
